import type React from "react";
import { useEffect, useMemo, useState } from "react";

import type { LanguageDto } from "@/api/types";
import { FormRadioCardGroup } from "@/components/Form/FormRadioCard";
import type { ModalBaseProps } from "@/components/Modal/Modal";
import { Modal } from "@/components/Modal/Modal";
import { RadioCard } from "@/components/Radio/RadioCard";
import { SearchInput } from "@/components/SearchInput/SearchInput";
import { Box } from "@/design-system/Box";
import { Button } from "@/design-system/Button";
import { Stack } from "@/design-system/Stack";
import { getFlagIcon } from "@/helpers/flags";
import { useTranslation } from "@/translations";

type AutoTranslationLanguageSettingModalProps = {
  selectedLanguage: LanguageDto;
  languages: LanguageDto[];
  onSelectLanguage: (language: LanguageDto) => void | Promise<void>;
  isSubmitting?: boolean;
} & ModalBaseProps;

/** Lets users search and select a content language, closing only after the selection succeeds. */
export function AutoTranslationLanguageSettingModal({
  languages,
  selectedLanguage,
  isOpened,
  isSubmitting,
  onOpenChange,
  onSelectLanguage,
}: AutoTranslationLanguageSettingModalProps): React.ReactNode {
  const [currSelectedLanguage, setCurrSelectedLanguage] = useState<LanguageDto>(selectedLanguage);
  const [searchQuery, setSearchQuery] = useState("");
  const { t, i18n } = useTranslation();

  useEffect(() => {
    // oxlint-disable-next-line react-hooks/set-state-in-effect
    setCurrSelectedLanguage(selectedLanguage);
    setSearchQuery("");
  }, [isOpened, selectedLanguage]);

  const visibleLanguages = useMemo(
    () => filterAndSortLanguages(languages, searchQuery, i18n.language),
    [i18n.language, languages, searchQuery],
  );

  const handleConfirmSelection = async () => {
    await onSelectLanguage(currSelectedLanguage);

    onOpenChange(false);
  };

  return (
    <Modal.Root
      data-testid="auto-translation-language-setting-modal"
      isOpened={isOpened}
      onOpenChange={onOpenChange}
      title={t("page.create-user.auto-translation-setting-modal.title")}
      description={t("page.create-user.auto-translation-setting-modal.description")}
      height={680}
    >
      <Modal.Body>
        <Stack gap="md">
          <Box padding={{ top: "md" }}>
            <SearchInput
              aria-label={t("common.action.search")}
              placeholder={t("common.action.search")}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
          </Box>
          <FormRadioCardGroup layout="vertical">
            {visibleLanguages.map((language) => (
              <RadioCard
                key={language.id}
                icon={getFlagIcon({ languageId: language.id, width: 24 })}
                name="languageId"
                description={language.previewText}
                label={language.description}
                value={language.id}
                checked={currSelectedLanguage?.id === language.id}
                onChange={() => setCurrSelectedLanguage(language)}
              />
            ))}
          </FormRadioCardGroup>
        </Stack>
      </Modal.Body>
      <Modal.Actions isSticky>
        <Modal.Close>
          <Button variant="outline" disabled={isSubmitting}>
            {t("common.action.close")}
          </Button>
        </Modal.Close>
        <Button onClick={handleConfirmSelection} data-testid="select-btn" isLoading={isSubmitting}>
          {t("common.action.select")}
        </Button>
      </Modal.Actions>
    </Modal.Root>
  );
}

/**
 * Searches display names, native aliases, localized preview text, and language IDs using Unicode-normalized text.
 * Native names use Intl.DisplayNames with deterministic fallbacks; matches are locale-sorted by display name.
 */
export function filterAndSortLanguages(languages: LanguageDto[], searchQuery: string, locale: string): LanguageDto[] {
  const collator = new Intl.Collator(locale, { numeric: true, sensitivity: "base" });
  const normalizedQuery = normalizeSearchValue(searchQuery);

  return languages
    .filter((language) => {
      if (!normalizedQuery) {
        return true;
      }

      return [language.description, ...getLanguageSearchAliases(language.id), language.previewText, language.id].some(
        (value) => normalizeSearchValue(value).includes(normalizedQuery),
      );
    })
    .toSorted((first, second) => {
      if (normalizedQuery) {
        const firstStartsWithQuery = normalizeSearchValue(first.description).startsWith(normalizedQuery);
        const secondStartsWithQuery = normalizeSearchValue(second.description).startsWith(normalizedQuery);

        if (firstStartsWithQuery !== secondStartsWithQuery) {
          return firstStartsWithQuery ? -1 : 1;
        }
      }

      return collator.compare(first.description, second.description) || collator.compare(first.id, second.id);
    });
}

const languageSearchAliases: Partial<Record<string, string[]>> = {
  be: ["беларуская"],
  cs: ["čeština"],
  cy: ["Cymraeg"],
  fi: ["suomi"],
  gn: ["Avañe'ẽ"],
  hi: ["हिन्दी"],
  is: ["íslenska"],
  ka: ["ქართული"],
  ky: ["кыргызча"],
  la: ["latina"],
  ml: ["മലയാളം"],
  nb: ["norsk bokmål"],
  sa: ["संस्कृत भाषा"],
  sk: ["slovenčina"],
  sl: ["slovenščina"],
  tl: ["Filipino"],
  xh: ["isiXhosa"],
  zu: ["isiZulu"],
};

function getLanguageSearchAliases(languageId: string): string[] {
  try {
    const nativeName = new Intl.DisplayNames([languageId], { type: "language" }).of(languageId);

    return [...(nativeName ? [nativeName] : []), ...(languageSearchAliases[languageId] ?? [])];
  } catch {
    return languageSearchAliases[languageId] ?? [];
  }
}

function normalizeSearchValue(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replaceAll(/\p{Diacritic}/gu, "")
    .replaceAll(/[‘’ʻʼ]/gu, "'");
}
